import { isPlatformServer, NgClass, NgStyle } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  input,
  Input,
  OnDestroy,
  Output,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import {
  FormsModule,
  NgControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { createMask, InputMaskModule } from '@ngneat/input-mask';
import * as onScan from 'onscan.js';
import { debounceTime, fromEvent, Subject, takeUntil } from 'rxjs';
import { IconComponent } from '../icon/icon.component';
import { TranslateModule } from '@ngx-translate/core';
import { InputBaseComponent } from 'src/app/_core';

export type TLabelMode =
  | 'PRIMARY'
  | 'SECONDARY'
  | 'E-COMMERCE'
  | 'E-COMMERCE_SECONDARY';
type TInputMode = 'REGULAR' | 'PIN';
export type TLabelSize = 'MEDIUM' | 'SMALL';
type TFontSize = 'MEDIUM' | 'SMALL';
type TIconMode = 'INSIDE' | 'OUTSIDE';

@Component({
  standalone: true,
  selector: 'neopos-input-text',
  styles: [
    `
      :host {
        display: block;
      }
      input::-webkit-outer-spin-button,
      input::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
      input[type='number'] {
        -moz-appearance: textfield;
      }
    `,
  ],
  templateUrl: './input-text.component.html',
  imports: [
    FormsModule,
    IconComponent,
    InputMaskModule,
    NgClass,
    NgStyle,
    ReactiveFormsModule,
    TranslateModule,
  ],
})
export class InputTextComponent
  extends InputBaseComponent<string>
  implements OnDestroy, AfterViewInit
{
  @ViewChild('input') input: ElementRef<HTMLInputElement> | undefined;

  @Input({ required: true }) inputId!: string;
  @Input() type = 'text';
  @Input() bottomText = '';
  @Input() showRequired = true;
  @Input() label = '';
  @Input() placeholder = '';
  @Input() showError = true;
  @Input() iconName = '';
  @Input() padding: 'very-small' | 'small' | 'normal' | 'medium' = 'normal';
  @Input() maxlength = 999;
  @Input() labelMode: TLabelMode = 'PRIMARY';
  @Input() labelSize: TLabelSize = 'MEDIUM';
  @Input() fontSize: TFontSize = 'MEDIUM';
  @Input() mode: TInputMode = 'REGULAR';
  @Input() mutationError = false;
  @Input() width!: number;
  @Input() iconMode: TIconMode = 'INSIDE';
  @Input() inputMask = false;
  @Input() set inputMaskType(value: string) {
    if (value === 'ip') {
      this.inputMaskValue = createMask({
        alias: 'ip',
        placeholder: 'x.x.x.x',
      });
    } else {
      this.inputMaskValue = createMask({
        alias: 'numeric',
        groupSeparator: '',
        digits: 2,
        digitsOptional: false,
        prefix: '',
      });
    }
  }
  @Input() autoFocus = false;
  @Input() disabledMode = false;
  @Input() labelMargin = 0;
  @Input() isNumber = false;
  @Input() allowScan = false;
  @Input() disableShowPassword = false;
  @Input() additionalText = '';
  @Input() additionalTextStyle = 'primary';
  @Input() themePadding: string = '';
  @Input() errorAbsolute = false;

  errorColor = input<string>();
  isFocusOutline = input<boolean>(true);
  ariaLabel = input<string>();
  autocomplete = input<string>();
  showBorder = input<boolean>(true);
  showRadius = input<boolean>(true);
  isTextCenter = input<boolean>(false);
  height = input<number>();

  @Output() focus: EventEmitter<FocusEvent> = new EventEmitter();
  @Output() blur: EventEmitter<FocusEvent> = new EventEmitter();
  @Output() scanEmitter: EventEmitter<void> = new EventEmitter();
  @Output() keydown = new EventEmitter<KeyboardEvent>();

  isPasswordVisible = signal(false);

  inputMaskValue = createMask({
    alias: 'numeric',
    groupSeparator: '',
    digits: 2,
    digitsOptional: false,
    prefix: '',
    rightAlign: false,
    inputType: 'number',
  });

  constructor(
    @Inject(PLATFORM_ID) private readonly _platformId: Object,
    public override ngControl: NgControl
  ) {
    super(ngControl);
  }

  ngAfterViewInit() {
    this._setAutofocus();
    this._handleOnScan(this.input?.nativeElement);
  }

  get isRequired() {
    if (this.showRequired) {
      return this.ngControl?.control?.hasValidator(Validators.required);
    } else {
      return false;
    }
  }

  get inputType() {
    if (this.type === 'password') {
      return this.isPasswordVisible() ? 'text' : 'password';
    }

    return this.type;
  }

  private _destroy$ = new Subject<void>();

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();

    if (!isPlatformServer(this._platformId)) {
      if (onScan.default.isAttachedTo(document)) {
        onScan.default.detachFrom(document);
      }
    }
  }

  onBlur() {
    this.blur.emit();

    if (this.ngControl.control?.value) {
      // this.ngControl.control?.setValue(
      //   trimInnerSpaces(this.ngControl.control.value)
      // );
    }
  }

  onConvertToNumber() {
    this.ngControl.control?.setValue(
      this._convertToNumber(this.ngControl.control.value)
    );
  }

  public getDescribedBy(): string | null {
    const ids = [];
    if (this.bottomText) ids.push(this.inputId + '-hint');
    if (this.showError && this.error) ids.push(this.inputId + '-error');
    return ids.length ? ids.join(' ') : null;
  }

  private _convertToNumber(input: string | number): any {
    if (typeof input === 'string') {
      const numericString = input.replace(/[^\d.]/g, '');
      return Number(numericString);
    }

    return input;
  }

  private _handleOnScan(element: HTMLInputElement | undefined) {
    if (!this.allowScan || !element) return;

    if (!isPlatformServer(this._platformId)) {
      if (!onScan.default.isAttachedTo(document)) {
        onScan.default.attachTo(document);
      }

      document.addEventListener('scan', () => {
        this.scanEmitter.emit();
      });
    }

    fromEvent(element, 'paste')
      .pipe(debounceTime(200), takeUntil(this._destroy$))
      .subscribe(() => {
        this.scanEmitter.emit();
      });
  }

  private _setAutofocus() {
    if (!this.autoFocus || !this.input) return;

    this.input?.nativeElement.focus();
  }
}
